import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import { getTrans, TranslationKey } from 'modules/i18n';
import { apiErrorMessage } from '../hooks';
import { useUpdateAvailabilityMutation } from '../query';
import { AvailabilityDay } from '../types';
import { toUpdateDays, validateWeekRanges } from '../utils';
import { DayPatternEditor } from './DayPatternEditor';
import { EditorDialog } from './EditorDialog';

interface Props {
  open: boolean;
  onClose: () => void;
  initialDays: AvailabilityDay[];
  onSaved: () => void;
}

/** Full-replace editor for the weekly availability template. */
export const TemplateEditorDialog = ({
  open,
  onClose,
  initialDays,
  onSaved
}: Props) => {
  const [days, setDays] = useState<AvailabilityDay[]>(initialDays);
  const [error, setError] = useState<string | null>(null);
  const mutation = useUpdateAvailabilityMutation();

  useEffect(() => {
    if (open) {
      setDays(initialDays);
      setError(null);
    }
  }, [open, initialDays]);

  const handleSave = () => {
    const validationError = validateWeekRanges(days);
    if (validationError) {
      setError(
        getTrans(
          `availabilityPage.validation.${validationError}` as TranslationKey
        )
      );
      return;
    }

    mutation.mutate(
      { days: toUpdateDays(days) },
      {
        onSuccess: () => {
          onSaved();
          onClose();
        },
        onError: responseError => setError(apiErrorMessage(responseError))
      }
    );
  };

  return (
    <EditorDialog
      open={open}
      title={getTrans('availabilityPage.templateEditor.title')}
      onClose={onClose}
      onSave={handleSave}
      saving={mutation.isLoading}
      dataTestId="template-editor"
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {getTrans('availabilityPage.templateEditor.hint')}
      </Typography>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          data-testid="template-editor-error"
        >
          {error}
        </Alert>
      )}
      <DayPatternEditor days={days} onChange={setDays} />
    </EditorDialog>
  );
};
